/**
 * A workspace's pointer to a recipe that lives in a room (PLAN-COLLAB A2).
 *
 * A shared recipe is not in `state.recipes`. It lives in a room of its own, one
 * document every member writes to, and each member's workspace holds only this
 * reference to it, keyed by the recipe's id. That split is what lets the
 * workspace go on being one person's: nothing another member does ever lands
 * in it, and the reference carries only what is *mine* about the recipe.
 *
 * Mine is three things. The role, because the owner pays for the room and the
 * cap counts owned references (D-CO4). When I joined, for the list's order.
 * And my equipment (D-CO3): `null` follows the room's shared default, an id is
 * a profile in *this* workspace that I brew the recipe on and nobody else sees.
 *
 * The commands that move a recipe across the boundary (`recipe.share`,
 * `sharedRecipe.join`, `sharedRecipe.leave`) are issued by the Worker alone,
 * because each follows a change in D1 membership the client cannot make on its
 * own; {@link SERVER_ONLY_COMMANDS} is the list the socket refuses.
 */

import { type CommandError, commandError } from '../commands/envelope';
import { recipeScopeOf } from '../commands/scope';
import type { Command, CommandType } from '../commands/types';
import type { EquipmentProfileDoc } from './equipment';
import type { RecipeDoc } from './recipe';
import type { RecipeSnapshotDoc } from './recipe-snapshot';
import type { SharedRecipeRef } from './shared-recipe-ref';
import { emptyWorkspace, type WorkspaceState } from './workspace';

export { type SharedRecipeRef, sharedRecipeRefSchema } from './shared-recipe-ref';

/** Commands only the Worker issues; a socket push of one is refused. */
export const SERVER_ONLY_COMMANDS: ReadonlySet<CommandType> = new Set<CommandType>([
  'recipe.share',
  'sharedRecipe.join',
  'sharedRecipe.leave',
]);

/**
 * `recipe` with every ingredient link this workspace cannot resolve cut.
 *
 * The leaver's copy (D-CO7) was written in a room, where a row may link to an
 * entry in *another* member's library. Here that id names nothing, so it goes;
 * a link to an entry this workspace does have is the leaver's own and stays.
 * Unlike `stripIngredientLinks`, which cuts every link, this keeps the ones
 * that resolve. Rows already fine are returned by reference.
 */
export function keepResolvableIngredientLinks(
  recipe: RecipeDoc,
  customIngredients: Readonly<Record<string, unknown>>,
): RecipeDoc {
  const keep = <T extends { readonly ingredientId: string | null }>(items: readonly T[]): T[] =>
    items.map((item) =>
      item.ingredientId === null || Object.hasOwn(customIngredients, item.ingredientId)
        ? item
        : { ...item, ingredientId: null },
    );
  return {
    ...recipe,
    fermentables: keep(recipe.fermentables),
    hops: keep(recipe.hops),
    yeasts: keep(recipe.yeasts),
    miscs: keep(recipe.miscs),
  };
}

// ---------------------------------------------------------------------------
// The room: its first state and its rules (D-CO2)
// ---------------------------------------------------------------------------

/**
 * The shared default's id inside a room, and in the merged view.
 *
 * Derived from the recipe id rather than minted, so both sides of the wire name
 * the same profile without asking, and the merged view can hold every room's
 * default beside the workspace's own profiles without a clash. `se.` is safe as
 * a namespace: client ids are `<prefix>-<uuid>` (`lib/ids.ts`, `eq-` for
 * equipment) and built-ins are `builtin-…`, so nothing else starts with it.
 * Sliced to the 64 characters a document id may hold.
 */
export function sharedEquipmentId(recipeId: string): string {
  return `se.${recipeId}`.slice(0, 64);
}

/** Whether `id` names a room's shared default rather than a profile of the workspace's own. */
export function isSharedEquipmentId(id: string): boolean {
  return id.startsWith('se.');
}

/**
 * A room's first state: `emptyWorkspace()` plus one recipe, its snapshots, and
 * its shared default.
 *
 * The equipment arrives as a *copy* (D-CO3): whatever profile the owner had on
 * the recipe when they shared it, now under {@link sharedEquipmentId} and with
 * `builtin: false`. The flag matters when the owner shared a recipe on a
 * built-in kit. A built-in refuses `equipment.update`, and the room's default
 * has to be editable, because that is the only equipment command it takes.
 */
export function roomStateFrom(args: {
  readonly recipe: RecipeDoc;
  readonly snapshots: readonly RecipeSnapshotDoc[];
  readonly equipment: EquipmentProfileDoc | null;
}): WorkspaceState {
  const base = emptyWorkspace();
  const equipmentId = sharedEquipmentId(args.recipe.id);
  const recipe: RecipeDoc = {
    ...args.recipe,
    equipmentProfileId: args.equipment === null ? null : equipmentId,
  };
  const equipmentProfiles =
    args.equipment === null
      ? base.equipmentProfiles
      : {
          ...base.equipmentProfiles,
          [equipmentId]: { ...args.equipment, id: equipmentId, builtin: false },
        };
  return {
    ...base,
    recipes: { [recipe.id]: recipe },
    recipeSnapshots: Object.fromEntries(args.snapshots.map((snapshot) => [snapshot.id, snapshot])),
    equipmentProfiles,
  };
}

/**
 * Whether `command` is about the shared recipe `recipeId`, by the room's three
 * rules. The one statement of those rules: the room asks it to refuse, the
 * workspace asks it to route.
 *
 * `roomStates` is where a snapshot the local state lacks is looked for; see
 * {@link commandRoom} for why the workspace needs it.
 */
function isRoomCommand(
  recipeId: string,
  state: WorkspaceState,
  command: Command,
  roomStates?: Readonly<Record<string, WorkspaceState>>,
): boolean {
  switch (command.type) {
    // Creating, restoring and deleting the recipe are not edits to it. A room
    // is made by the Worker from a share, never by a command, and deletion is
    // the owner's "delete for everyone", which goes through D1 (D-CO8a).
    case 'recipe.create':
    case 'recipe.restore':
    case 'recipe.delete':
      return false;

    // The snapshot documents carry their recipe; the other two name an id, so
    // the recipe is whoever the snapshot already belongs to. Restore reads its
    // payload rather than the state, because undoing a delete restores a
    // snapshot the state no longer has.
    case 'recipeSnapshot.create':
      return command.payload.from.recipeId === recipeId;
    case 'recipeSnapshot.restore':
      return command.payload.snapshot.recipeId === recipeId;
    case 'recipeSnapshot.rename':
    case 'recipeSnapshot.delete': {
      const id = command.payload.snapshotId;
      const snapshot = state.recipeSnapshots[id] ?? roomStates?.[recipeId]?.recipeSnapshots[id];
      return snapshot?.recipeId === recipeId;
    }

    // The shared default, and nothing else. `delete` and `restore` stay out:
    // the picker replaces the default by writing over it, never by removing it.
    case 'equipment.create':
    case 'equipment.update':
      return command.payload.profileId === sharedEquipmentId(recipeId);

    // The room's recipe points at its shared default or at nothing (D-CO3).
    // Any other id names a profile in somebody's workspace, which the room
    // does not hold and the other members cannot see; "Just for me" is the
    // member's own override, written to their workspace reference instead.
    case 'recipe.setEquipment':
      return (
        command.payload.recipeId === recipeId &&
        (command.payload.equipmentProfileId === null ||
          command.payload.equipmentProfileId === sharedEquipmentId(recipeId))
      );

    default:
      return recipeScopeOf(command) === recipeId;
  }
}

/**
 * `null` if a room holding `recipeId` accepts `command`; else the refusal.
 *
 * `invalid-state`, because the command may be perfectly good in a workspace:
 * it is only meaningless *here*, which is what that code says (envelope.ts).
 */
export function roomRefusal(
  recipeId: string,
  state: WorkspaceState,
  command: Command,
): CommandError | null {
  if (isRoomCommand(recipeId, state, command)) return null;
  return commandError(
    'invalid-state',
    'That change is for something other than this shared recipe, so we can’t make it here.',
    {
      commandId: command.commandId,
      commandType: command.type,
    },
  );
}

/**
 * Which room a command belongs to, given the workspace's references; `null`
 * is the workspace itself.
 *
 * The same rules as {@link roomRefusal}, asked of every key of
 * `state.sharedRecipes`, so anything this sends to a room the room accepts.
 *
 * `roomStates` exists for the snapshot commands that name only an id
 * (`rename`, `delete`). Their recipe is found from the snapshot, and the
 * workspace's own state does not have a shared recipe's snapshots: sharing
 * moved them to the room. The merged view does (it copies them back in), so a
 * caller dispatching against {@link overlaySharedRecipes} needs no second
 * argument; a caller holding the bare workspace passes the rooms it holds, and
 * each room is searched for its own snapshots.
 */
export function commandRoom(
  state: WorkspaceState,
  command: Command,
  roomStates?: Readonly<Record<string, WorkspaceState>>,
): string | null {
  for (const recipeId of Object.keys(state.sharedRecipes)) {
    if (isRoomCommand(recipeId, state, command, roomStates)) return recipeId;
  }
  return null;
}

// ---------------------------------------------------------------------------
// The merged view (D-CO3, D-CO11b)
// ---------------------------------------------------------------------------

/**
 * The workspace with every caught-up room's recipe laid over it: the state the
 * UI reads and the assistant is shown.
 *
 * A room has caught up when it holds the recipe; one that has not is skipped,
 * and the id pages treat that recipe as loading (D-CO11c). The recipe's
 * `equipmentProfileId` is the *effective* profile (D-CO3): my override if that
 * profile still exists here, else the room's shared default if it has one,
 * else nothing. The shared default itself is copied in whichever wins, so the
 * picker can offer it.
 *
 * Returns `workspace` itself when there is nothing to lay over, and otherwise
 * replaces only `recipes`, `recipeSnapshots` and `equipmentProfiles`. Selectors
 * memoise on identity, and a workspace with no shared recipes must not look
 * changed on every render.
 */
export function overlaySharedRecipes(
  workspace: WorkspaceState,
  rooms: Readonly<Record<string, WorkspaceState>>,
): WorkspaceState {
  let recipes: Record<string, RecipeDoc> | null = null;
  let recipeSnapshots: Record<string, RecipeSnapshotDoc> | null = null;
  let equipmentProfiles: Record<string, EquipmentProfileDoc> | null = null;

  for (const [recipeId, ref] of Object.entries(workspace.sharedRecipes)) {
    const room = rooms[recipeId];
    const recipe = room?.recipes[recipeId];
    if (room === undefined || recipe === undefined) continue;
    recipes ??= { ...workspace.recipes };
    recipeSnapshots ??= { ...workspace.recipeSnapshots };
    equipmentProfiles ??= { ...workspace.equipmentProfiles };

    const defaultId = sharedEquipmentId(recipeId);
    const shared = room.equipmentProfiles[defaultId];
    if (shared !== undefined) equipmentProfiles[defaultId] = shared;
    recipes[recipeId] = {
      ...recipe,
      equipmentProfileId: effectiveEquipmentId(ref, workspace, shared),
    };
    // Only the room's own recipe's snapshots, and never over an id the
    // workspace already holds: the room is another member's writing, and the
    // merged view must not let it replace a document of mine.
    for (const snapshot of Object.values(room.recipeSnapshots)) {
      if (snapshot.recipeId !== recipeId) continue;
      if (Object.hasOwn(workspace.recipeSnapshots, snapshot.id)) continue;
      recipeSnapshots[snapshot.id] = snapshot;
    }
  }

  if (recipes === null || recipeSnapshots === null || equipmentProfiles === null) return workspace;
  return { ...workspace, recipes, recipeSnapshots, equipmentProfiles };
}

/** D-CO3: my override if it still exists, else the room's shared default, else none. */
function effectiveEquipmentId(
  ref: SharedRecipeRef,
  workspace: WorkspaceState,
  shared: EquipmentProfileDoc | undefined,
): string | null {
  const override = ref.equipmentOverrideId;
  if (override !== null && Object.hasOwn(workspace.equipmentProfiles, override)) return override;
  return shared?.id ?? null;
}

/**
 * The number the recipe cap judges (D-CO4): own recipes plus owned
 * references, never editor references.
 *
 * Written to give the same answer on the merged view as on the bare
 * workspace, because both reach it: the client checks the cap against what it
 * shows, the Durable Object against what it stores. In the merged view a
 * shared recipe is in `recipes` *and* in `sharedRecipes`, so a recipe id
 * counts once whichever map it turns up in, and an editor's never counts.
 */
export function capRecipeCount(state: WorkspaceState): number {
  let count = 0;
  for (const recipeId of Object.keys(state.recipes)) {
    if (state.sharedRecipes[recipeId]?.role !== 'editor') count += 1;
  }
  for (const [recipeId, ref] of Object.entries(state.sharedRecipes)) {
    if (ref.role === 'owner' && !Object.hasOwn(state.recipes, recipeId)) count += 1;
  }
  return count;
}
